import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Activity, TrendingUp, Clock, CheckCircle2, AlertCircle, Cog, RefreshCw, Download, Mail, CalendarDays, Printer, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMinutes = (m) => {
  if (!m && m !== 0) return "0m";
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const oeeColor = (val) => {
  if (val >= 85) return "text-green-600";
  if (val >= 60) return "text-amber-600";
  return "text-red-600";
};

const oeeBg = (val) => {
  if (val >= 85) return "bg-green-500";
  if (val >= 60) return "bg-amber-500";
  return "bg-red-500";
};

const oeeLabel = (val) => {
  if (val >= 85) return "Classe Mundial";
  if (val >= 60) return "Aceitavel";
  if (val >= 40) return "Baixo";
  return "Critico";
};

function KPI({ title, value, suffix = "%", icon: Icon, threshold = "oee", target = 85 }) {
  const colorClass = threshold === "oee" ? oeeColor(value) : "text-blue-700";
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
          {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className={`text-3xl font-bold ${colorClass}`}>
          {value}{suffix}
        </div>
        <div className="text-xs text-gray-500 mt-1">Alvo: {target}{suffix}</div>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ value, max = 100 }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div className={`${oeeBg(value)} h-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function OEEDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(7);

  const fetchOEE = useCallback(async () => {
    setLoading(true);
    try {
      const end = new Date();
      const start = new Date(Date.now() - period * 24 * 60 * 60 * 1000);
      const res = await axios.get(`${API}/oee/dashboard`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        },
      });
      setData(res.data);
    } catch (e) {
      console.error("Erro a carregar OEE:", e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchOEE(); }, [fetchOEE]);

  const sendEmailNow = async () => {
    if (!window.confirm("Enviar agora o resumo OEE diario para geral@engenharia3d.pt?")) return;
    try {
      await axios.post(`${API}/oee/send-email-now`);
      toast.success("Email OEE enviado para geral@engenharia3d.pt");
    } catch (e) {
      showError(e, "Erro a enviar email OEE");
    }
  };

  const sendWeeklyEmailNow = async () => {
    if (!window.confirm("Enviar agora o RESUMO SEMANAL OEE (ultimos 7 dias) para geral@engenharia3d.pt?")) return;
    try {
      await axios.post(`${API}/oee/send-weekly-email-now`);
      toast.success("Resumo Semanal OEE enviado para geral@engenharia3d.pt");
    } catch (e) {
      showError(e, "Erro a enviar email semanal");
    }
  };

  const sendFullReportNow = async () => {
    if (!window.confirm("Enviar agora o RELATÓRIO COMPLETO (Excel com Encomendas, Peças, Moldes, OM, SPC, Custeio) para os destinatários configurados?")) return;
    try {
      await axios.post(`${API}/reports/full-report/send-now`);
      toast.success("Relatório completo enviado por email");
    } catch (e) {
      showError(e, "Erro a enviar relatório completo");
    }
  };

  const downloadFullReport = async () => {
    try {
      const r = await axios.get(`${API}/excel/full-report?days=30`, { responseType: "blob" });
      const disp = r.headers["content-disposition"] || "";
      const match = disp.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : `relatorio-completo-${Date.now()}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório completo transferido");
    } catch (e) {
      showError(e, "Erro a gerar relatório");
    }
  };

  const printWeeklyReport = async () => {
    setLoading(true);
    try {
      const end = new Date();
      const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const res = await axios.get(`${API}/oee/dashboard`, {
        params: { start_date: start.toISOString(), end_date: end.toISOString() },
      });
      const weeklyData = res.data;
      const win = window.open("", "_blank");
      if (!win) { toast.error("Permita popups para imprimir"); return; }
      const g = weeklyData.global;
      const oeeColorH = (v) => v >= 85 ? "#10b981" : v >= 60 ? "#f59e0b" : "#ef4444";
      const today = new Date().toLocaleString("pt-PT");
      // Worst-first ranking
      const ranking = [...(weeklyData.by_machine || [])].sort((a, b) => a.oee - b.oee).slice(0, 5);
      const rankingHtml = ranking.map((m, i) => `
        <tr><td style="font-weight:bold;color:#b45309">#${i + 1}</td>
        <td><strong>${m.machine_code}</strong><br><small style="color:#666">${m.machine_name}</small></td>
        <td style="text-align:right;color:${oeeColorH(m.oee)};font-weight:bold">${m.oee}%</td>
        <td style="text-align:right">${m.availability}%</td><td style="text-align:right">${m.quality}%</td>
        <td style="text-align:right;color:#16a34a">${m.good_qty.toLocaleString()}</td>
        <td style="text-align:right;color:#dc2626">${m.rejected_qty.toLocaleString()}</td>
        <td style="text-align:right">${formatMinutes(m.stop_minutes)}</td></tr>`).join("");
      const allMachinesHtml = (weeklyData.by_machine || []).map(m => `
        <tr><td><strong>${m.machine_code}</strong><br><small style="color:#666">${m.machine_name}</small></td>
        <td style="text-align:right">${m.availability}%</td><td style="text-align:right">${m.performance}%</td>
        <td style="text-align:right">${m.quality}%</td>
        <td style="text-align:right;color:${oeeColorH(m.oee)};font-weight:bold">${m.oee}%</td>
        <td style="text-align:right;color:#16a34a">${m.good_qty.toLocaleString()}</td>
        <td style="text-align:right;color:#dc2626">${m.rejected_qty.toLocaleString()}</td>
        <td style="text-align:right">${formatMinutes(m.stop_minutes)}</td></tr>`).join("");
      const stopsHtml = (weeklyData.top_stop_reasons || []).map(s => `<li><strong>${s.reason}</strong>: ${formatMinutes(s.minutes)}</li>`).join("") || "<li>Sem paragens registadas</li>";
      const trendHtml = (weeklyData.daily_trend || []).map(d => `
        <tr><td style="font-family:monospace">${d.date}</td>
        <td style="text-align:right">${d.availability}%</td><td style="text-align:right">${d.performance}%</td>
        <td style="text-align:right">${d.quality}%</td>
        <td style="text-align:right;color:${oeeColorH(d.oee)};font-weight:bold">${d.oee}%</td>
        <td style="text-align:right">${d.good_qty}</td><td style="text-align:right">${d.rejected_qty}</td></tr>`).join("");
      
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resumo Semanal OEE</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;padding:24px;max-width:1100px;margin:0 auto;color:#333;font-size:13px}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e3a8a;padding-bottom:12px;margin-bottom:18px}
h1{font-size:22px;color:#1e3a8a;margin:0}
.subtitle{color:#666;font-size:12px;margin-top:4px}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px}
.kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px;text-align:center}
.kpi .label{font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;font-weight:600}
.kpi .value{font-size:30px;font-weight:700;margin-top:4px}
.section{margin:20px 0;page-break-inside:avoid}
h2{font-size:15px;color:#1e3a8a;border-bottom:2px solid #e5e7eb;padding-bottom:5px;margin:0 0 10px}
.callout-warn{background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:14px;margin:14px 0}
.callout-warn h3{margin:0 0 8px;color:#92400e;font-size:14px}
table{width:100%;border-collapse:collapse;font-size:12px}
th{background:#f3f4f6;padding:7px;text-align:left;font-size:10px;text-transform:uppercase;color:#555;border-bottom:2px solid #e5e7eb}
td{padding:7px;border-bottom:1px solid #f0f0f0}
.summary{background:#eff6ff;border:1px solid #dbeafe;border-radius:8px;padding:12px;margin-bottom:16px}
.footer{margin-top:24px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:10px;color:#999;text-align:center}
@media print{body{padding:14px;font-size:11px}.kpi .value{font-size:24px}}
</style></head><body>
<div class="header">
  <div style="display:flex;align-items:center;gap:10px">
    <img src="/ep3d-logo.png" onerror="this.style.display='none'" style="height:48px">
    <div>
      <h1>Resumo Semanal OEE</h1>
      <div class="subtitle">EP3D ENGINEERING PROJECT | NIF: 517 316 188 | Reuniao Chefia</div>
    </div>
  </div>
  <div style="text-align:right;font-size:11px;color:#666">
    <div><strong>Periodo:</strong> ${new Date(weeklyData.period.start).toLocaleDateString("pt-PT")} - ${new Date(weeklyData.period.end).toLocaleDateString("pt-PT")} (7 dias)</div>
    <div><strong>Gerado:</strong> ${today}</div>
  </div>
</div>

<div class="kpi-grid">
  <div class="kpi"><div class="label">OEE Global</div><div class="value" style="color:${oeeColorH(g.oee)}">${g.oee}%</div></div>
  <div class="kpi"><div class="label">Disponibilidade</div><div class="value">${g.availability}%</div></div>
  <div class="kpi"><div class="label">Performance</div><div class="value">${g.performance}%</div></div>
  <div class="kpi"><div class="label">Qualidade</div><div class="value">${g.quality}%</div></div>
</div>

<div class="summary">
  <strong>Producao da semana:</strong>
  <span style="color:#16a34a"><strong>${g.total_good.toLocaleString()}</strong> pecas boas</span> |
  <span style="color:#dc2626"><strong>${g.total_rejected.toLocaleString()}</strong> rejeitadas</span> |
  Total: <strong>${g.total_qty.toLocaleString()}</strong> pecas |
  Tempo paragens: <strong>${formatMinutes(g.total_stop_minutes)}</strong>
</div>

${ranking.length > 0 ? `<div class="section callout-warn">
  <h3>⚠️ Maquinas a Necessitar Atencao (Pior OEE da Semana)</h3>
  <table>
    <tr><th>Pos</th><th>Maquina</th><th style="text-align:right">OEE</th><th style="text-align:right">Disp.</th><th style="text-align:right">Qual.</th><th style="text-align:right">Boas</th><th style="text-align:right">Rejeit.</th><th style="text-align:right">Paragens</th></tr>
    ${rankingHtml}
  </table>
</div>` : ''}

<div class="section">
  <h2>Tendencia OEE Diaria</h2>
  <table>
    <tr><th>Data</th><th style="text-align:right">Disp.</th><th style="text-align:right">Perf.</th><th style="text-align:right">Qual.</th><th style="text-align:right">OEE</th><th style="text-align:right">Boas</th><th style="text-align:right">Rejeit.</th></tr>
    ${trendHtml}
  </table>
</div>

<div class="section">
  <h2>OEE por Maquina (Todas)</h2>
  <table>
    <tr><th>Maquina</th><th style="text-align:right">Disp.</th><th style="text-align:right">Perf.</th><th style="text-align:right">Qual.</th><th style="text-align:right">OEE</th><th style="text-align:right">Boas</th><th style="text-align:right">Rejeit.</th><th style="text-align:right">Paragens</th></tr>
    ${allMachinesHtml || '<tr><td colspan="8" style="text-align:center;color:#999;padding:14px">Sem dados</td></tr>'}
  </table>
</div>

<div class="section">
  <h2>Principais Causas de Paragem</h2>
  <ul>${stopsHtml}</ul>
</div>

<div class="footer">
  Documento gerado pelo Sistema MES EP3D para reuniao de chefia | ${today}
</div>
</body></html>`);
      win.document.close();
      setTimeout(() => win.print(), 600);
    } catch (e) {
      toast.error("Erro a gerar relatorio semanal");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = () => {
    if (!data) return;
    const win = window.open("", "_blank");
    if (!win) { toast.error("Permita popups para exportar PDF"); return; }
    const g = data.global;
    const oeeColorH = (v) => v >= 85 ? "#10b981" : v >= 60 ? "#f59e0b" : "#ef4444";
    const today = new Date().toLocaleString("pt-PT");
    const periodLabel = `${new Date(data.period.start).toLocaleDateString("pt-PT")} - ${new Date(data.period.end).toLocaleDateString("pt-PT")} (${data.period.days} dias)`;
    const machinesHtml = (data.by_machine || []).map(m => `
      <tr>
        <td><strong>${m.machine_code}</strong><br><small style="color:#666">${m.machine_name}</small></td>
        <td style="text-align:right">${m.availability}%</td>
        <td style="text-align:right">${m.performance}%</td>
        <td style="text-align:right">${m.quality}%</td>
        <td style="text-align:right;color:${oeeColorH(m.oee)};font-weight:bold">${m.oee}%</td>
        <td style="text-align:right;color:#16a34a">${m.good_qty.toLocaleString()}</td>
        <td style="text-align:right;color:#dc2626">${m.rejected_qty.toLocaleString()}</td>
        <td style="text-align:right">${formatMinutes(m.stop_minutes)}</td>
      </tr>
    `).join("");
    const stopsHtml = (data.top_stop_reasons || []).map(s => `<li><strong>${s.reason}</strong>: ${formatMinutes(s.minutes)}</li>`).join("") || "<li>Sem paragens registadas</li>";
    const trendHtml = (data.daily_trend || []).map(d => `
      <tr><td style="font-family:monospace">${d.date}</td><td style="text-align:right">${d.availability}%</td><td style="text-align:right">${d.performance}%</td><td style="text-align:right">${d.quality}%</td><td style="text-align:right;color:${oeeColorH(d.oee)};font-weight:bold">${d.oee}%</td><td style="text-align:right">${d.good_qty}</td><td style="text-align:right">${d.rejected_qty}</td></tr>
    `).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Relatorio OEE</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;padding:30px;max-width:1000px;margin:0 auto;color:#333;font-size:13px}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e3a8a;padding-bottom:15px;margin-bottom:25px}
.header img{height:50px}
h1{font-size:22px;color:#1e3a8a;margin:0}
.subtitle{color:#666;font-size:12px;margin-top:4px}
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px}
.kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:16px;text-align:center}
.kpi .label{font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;font-weight:600}
.kpi .value{font-size:32px;font-weight:700;margin-top:4px}
.section{margin:24px 0;page-break-inside:avoid}
h2{font-size:15px;color:#1e3a8a;border-bottom:2px solid #e5e7eb;padding-bottom:6px;margin:0 0 12px}
table{width:100%;border-collapse:collapse;font-size:12px}
th{background:#f3f4f6;padding:8px;text-align:left;font-size:10px;text-transform:uppercase;color:#555;border-bottom:2px solid #e5e7eb}
td{padding:8px;border-bottom:1px solid #f0f0f0}
.summary{background:#eff6ff;border:1px solid #dbeafe;border-radius:8px;padding:14px;margin-bottom:20px}
.summary p{margin:4px 0}
.footer{margin-top:30px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#999;text-align:center}
@media print{body{padding:15px;font-size:11px}.kpi .value{font-size:26px}}
</style></head><body>
<div class="header">
  <div style="display:flex;align-items:center;gap:12px"><img src="/ep3d-logo.png" onerror="this.style.display='none'"><div><h1>Relatorio OEE</h1><div class="subtitle">EP3D ENGINEERING PROJECT | NIF: 517 316 188</div></div></div>
  <div style="text-align:right;font-size:11px;color:#666">
    <div><strong>Periodo:</strong> ${periodLabel}</div>
    <div><strong>Gerado:</strong> ${today}</div>
  </div>
</div>

<div class="kpi-grid">
  <div class="kpi"><div class="label">OEE Global</div><div class="value" style="color:${oeeColorH(g.oee)}">${g.oee}%</div></div>
  <div class="kpi"><div class="label">Disponibilidade</div><div class="value">${g.availability}%</div></div>
  <div class="kpi"><div class="label">Performance</div><div class="value">${g.performance}%</div></div>
  <div class="kpi"><div class="label">Qualidade</div><div class="value">${g.quality}%</div></div>
</div>

<div class="summary">
  <p><strong>Pecas Boas:</strong> ${g.total_good.toLocaleString()} | <strong style="color:#dc2626">Rejeitadas:</strong> ${g.total_rejected.toLocaleString()} | <strong>Total:</strong> ${g.total_qty.toLocaleString()}</p>
  <p><strong>Tempo Paragens:</strong> ${formatMinutes(g.total_stop_minutes)} | <strong>Tempo Planeado:</strong> ${formatMinutes(g.total_planned_minutes)} | <strong>Maquinas Activas:</strong> ${g.active_machines}</p>
</div>

<div class="section">
  <h2>OEE por Maquina</h2>
  <table>
    <tr><th>Maquina</th><th style="text-align:right">Disp.</th><th style="text-align:right">Perf.</th><th style="text-align:right">Qual.</th><th style="text-align:right">OEE</th><th style="text-align:right">Boas</th><th style="text-align:right">Rejeit.</th><th style="text-align:right">Paragens</th></tr>
    ${machinesHtml || '<tr><td colspan="8" style="text-align:center;color:#999;padding:16px">Sem dados</td></tr>'}
  </table>
</div>

<div class="section">
  <h2>Principais Causas de Paragem</h2>
  <ul>${stopsHtml}</ul>
</div>

<div class="section">
  <h2>Tendencia Diaria</h2>
  <table>
    <tr><th>Data</th><th style="text-align:right">Disp.</th><th style="text-align:right">Perf.</th><th style="text-align:right">Qual.</th><th style="text-align:right">OEE</th><th style="text-align:right">Boas</th><th style="text-align:right">Rejeit.</th></tr>
    ${trendHtml}
  </table>
</div>

<div class="footer">
  Relatorio gerado pelo Sistema MES EP3D | ${today}<br>
  OEE = Disponibilidade x Performance x Qualidade
</div>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const g = data?.global || {};

  return (
    <div className="space-y-6" data-testid="oee-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-8 w-8 text-indigo-600" />
            Dashboard OEE
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Eficiencia Global do Equipamento (Disponibilidade x Performance x Qualidade)</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="oee-period-selector"
          >
            <option value={1}>Ultimas 24h</option>
            <option value={7}>Ultimos 7 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={90}>Ultimos 90 dias</option>
          </select>
          <Button onClick={fetchOEE} variant="outline" size="sm" data-testid="oee-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={exportPDF} variant="outline" size="sm" disabled={!data} data-testid="oee-export-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          <Button onClick={sendEmailNow} variant="outline" size="sm" data-testid="oee-send-email-btn" title="Enviar resumo agora para geral@engenharia3d.pt">
            <Mail className="h-4 w-4 mr-2" />
            Email Diario
          </Button>
          <Button onClick={printWeeklyReport} variant="outline" size="sm" data-testid="oee-print-weekly-btn" disabled={loading} className="border-amber-300 text-amber-700 hover:bg-amber-50" title="Imprimir resumo semanal para reuniao de chefia">
            <Printer className="h-4 w-4 mr-2" />
            Imprimir Semanal
          </Button>
          <Button onClick={sendWeeklyEmailNow} variant="outline" size="sm" data-testid="oee-send-weekly-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50" title="Enviar resumo semanal para geral@engenharia3d.pt">
            <CalendarDays className="h-4 w-4 mr-2" />
            Email Semanal
          </Button>
          <Button onClick={downloadFullReport} variant="outline" size="sm" data-testid="full-report-download-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Descarregar Excel completo (Encomendas, Peças, Moldes, OM, SPC, Custeio)">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel Completo
          </Button>
          <Button onClick={sendFullReportNow} variant="outline" size="sm" data-testid="full-report-send-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Enviar Relatorio Completo por email agora">
            <Mail className="h-4 w-4 mr-2" />
            Email Completo
          </Button>
        </div>
      </div>

      {loading && !data && (
        <Card><CardContent className="text-center py-12 text-gray-400">A carregar dados OEE...</CardContent></Card>
      )}

      {data && (
        <>
          {/* KPIs Globais */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="kpi-oee-global">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-indigo-600 uppercase tracking-wide">OEE Global</span>
                  <Activity className="h-5 w-5 text-indigo-600" />
                </div>
                <div className={`text-4xl font-bold ${oeeColor(g.oee || 0)}`}>
                  {g.oee || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1 font-semibold">{oeeLabel(g.oee || 0)}</div>
                <ProgressBar value={g.oee || 0} />
              </CardContent>
            </Card>
            <KPI title="Disponibilidade" value={g.availability || 0} icon={Clock} target={90} />
            <KPI title="Performance" value={g.performance || 0} icon={TrendingUp} target={95} />
            <KPI title="Qualidade" value={g.quality || 0} icon={CheckCircle2} target={99} />
          </div>

          {/* Resumo Producao */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 font-semibold">Pecas Boas</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{g.total_good?.toLocaleString() || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 font-semibold">Pecas Rejeitadas</div>
                <div className="text-2xl font-bold text-red-600 mt-1">{g.total_rejected?.toLocaleString() || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 font-semibold">Total Produzido</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{g.total_qty?.toLocaleString() || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 font-semibold">Tempo Paragens</div>
                <div className="text-2xl font-bold text-amber-600 mt-1">{formatMinutes(g.total_stop_minutes)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-gray-500 font-semibold">Maquinas Activas</div>
                <div className="text-2xl font-bold text-indigo-600 mt-1">{g.active_machines || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Por Maquina */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Cog className="h-5 w-5" />OEE por Maquina</CardTitle>
            </CardHeader>
            <CardContent>
              {(!data.by_machine || data.by_machine.length === 0) ? (
                <div className="text-center py-8 text-gray-400">Sem dados de produção no periodo seleccionado</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Maquina</TableHead>
                      <TableHead className="text-right">Disp. %</TableHead>
                      <TableHead className="text-right">Perf. %</TableHead>
                      <TableHead className="text-right">Qual. %</TableHead>
                      <TableHead className="text-right">OEE %</TableHead>
                      <TableHead className="text-right">Boas</TableHead>
                      <TableHead className="text-right">Rejeit.</TableHead>
                      <TableHead className="text-right">Paragens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_machine.map((m) => (
                      <TableRow key={m.machine_id} data-testid={`oee-machine-${m.machine_code}`}>
                        <TableCell className="font-mono font-semibold">{m.machine_code}<br/><span className="text-xs text-gray-500 font-normal">{m.machine_name}</span></TableCell>
                        <TableCell className="text-right font-medium">{m.availability}%</TableCell>
                        <TableCell className="text-right font-medium">{m.performance}%</TableCell>
                        <TableCell className="text-right font-medium">{m.quality}%</TableCell>
                        <TableCell className={`text-right font-bold ${oeeColor(m.oee)}`}>
                          <div className="flex items-center justify-end gap-2">
                            <span>{m.oee}%</span>
                            <span className={`inline-block w-2 h-2 rounded-full ${oeeBg(m.oee)}`} />
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-green-700">{m.good_qty.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-red-700">{m.rejected_qty.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-amber-700">{formatMinutes(m.stop_minutes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Top causas de paragem + Tendencia */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertCircle className="h-5 w-5 text-amber-600" />Principais Causas de Paragem</CardTitle></CardHeader>
              <CardContent>
                {(!data.top_stop_reasons || data.top_stop_reasons.length === 0) ? (
                  <div className="text-center py-6 text-gray-400 text-sm">Sem paragens registadas</div>
                ) : (
                  <div className="space-y-3">
                    {data.top_stop_reasons.map((s, i) => {
                      const max = Math.max(...data.top_stop_reasons.map(x => x.minutes), 1);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{s.reason}</span>
                            <span className="font-bold text-amber-700">{formatMinutes(s.minutes)}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div className="bg-amber-500 h-2 rounded-full" style={{ width: `${(s.minutes / max) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-5 w-5 text-indigo-600" />Tendencia OEE Diaria</CardTitle></CardHeader>
              <CardContent>
                {(!data.daily_trend || data.daily_trend.length === 0) ? (
                  <div className="text-center py-6 text-gray-400 text-sm">Sem dados</div>
                ) : (
                  <div className="space-y-2">
                    {data.daily_trend.slice(-7).map((d, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <span className="font-mono text-xs text-gray-500 w-20 shrink-0">{d.date.slice(5)}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                              <div className={`${oeeBg(d.oee)} h-full`} style={{ width: `${Math.min(100, d.oee)}%` }} />
                            </div>
                            <span className={`text-xs font-bold w-12 text-right ${oeeColor(d.oee)}`}>{d.oee}%</span>
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            D:{d.availability}% P:{d.performance}% Q:{d.quality}% | {d.good_qty} boas, {d.rejected_qty} rej.
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Formula explicativa */}
          <Card className="bg-slate-50 border-dashed">
            <CardContent className="p-4 text-xs text-gray-600">
              <div className="font-semibold text-gray-800 mb-2">Formulas:</div>
              <div><span className="font-mono bg-white px-2 py-0.5 rounded border">Disponibilidade</span> = (Tempo Planeado - Paragens) / Tempo Planeado</div>
              <div><span className="font-mono bg-white px-2 py-0.5 rounded border">Performance</span> = (Pecas Produzidas x Tempo de Ciclo Teorico) / Tempo Operacional</div>
              <div><span className="font-mono bg-white px-2 py-0.5 rounded border">Qualidade</span> = Pecas Boas / Pecas Totais</div>
              <div className="mt-1 font-semibold"><span className="font-mono bg-white px-2 py-0.5 rounded border">OEE</span> = Disponibilidade x Performance x Qualidade</div>
              <div className="mt-2 text-[11px]">Referencia: OEE &gt;= 85% = Classe Mundial | 60-85% = Aceitavel | &lt; 60% = Baixo</div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
